// ✅ ALTERED: ClassRegistration UI to match your portal theme (Orange header + blue borders)
// - Outer Paper: borderRadius 2 + blue border + overflow hidden
// - Orange gradient header bar
// - Body BG = #F2F2F2
// - Cards: white surfaces with blue outline + left orange accent stripe + zebra rows + hover
// - Buttons: orange primary, outlined blue secondary
// - Snackbars: rounded + consistent
// NOTE: logic unchanged (only UI/theme + small layout polish)

import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Typography,
  Button,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Chip,
  Stack,
} from "@mui/material";
import { API_URL } from "./config";

const ClassRegistration = () => {
  // ✅ Theme constants (same as other themed pages)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const SURFACE_BORDER = "rgba(25,118,210,0.25)";
  const ROW_ALT = "rgba(25,118,210,0.06)";

  const [logincampusCode, setCampusCode] = useState("");
  const [username, setUsername] = useState("");
  const [studentkey, setStudentKey] = useState("");
  const [availableCourses, setAvailableCourses] = useState([]);
  const [registeredCourses, setRegisteredCourses] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const token = localStorage.getItem("auth");

  // Fetch CampusCode & Username from JWT
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setCampusCode(decodedToken.campusCode);
        setUsername(decodedToken.username);
        setStudentKey(decodedToken.studentkey);
      } catch (err) {
        console.error("Error decoding token:", err);
        setError("Failed to decode authentication token.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch Available Courses + Registered Courses
  useEffect(() => {
    if (logincampusCode && username && studentkey) {
      fetchAvailableCourses();
      fetchRegisteredCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logincampusCode, username, studentkey]);

  const fetchAvailableCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/student/available-courses`, {
        params: { StudentKey: studentkey },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableCourses(response.data || []);
    } catch (err) {
      setError("Failed to fetch available courses.");
    }
  };

  const fetchRegisteredCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/student/registered-courses`, {
        params: { StudentKey: studentkey },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegisteredCourses(response.data || []);
    } catch (err) {
      setError("Failed to fetch registered courses.");
    }
  };

  const handleRegister = async (CourseID) => {
    try {
      await axios.post(
        `${API_URL}/api/student/register-course`,
        { StudentKey: studentkey, CourseID },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess("Successfully registered for the course!");
      fetchAvailableCourses();
      fetchRegisteredCourses();
    } catch (err) {
      setError("Failed to register for the course.");
    }
  };

  const handleDrop = async (CourseID) => {
    try {
      await axios.post(
        `${API_URL}/api/student/drop-course`,
        { StudentKey: studentkey, CourseID },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess("Successfully dropped the course.");
      fetchAvailableCourses();
      fetchRegisteredCourses();
    } catch (err) {
      setError("Failed to drop the course.");
    }
  };

  // ✅ common sx
  const surfacePaperSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${SURFACE_BORDER}`,
  };

  const sectionCardSx = {
    ...surfacePaperSx,
    p: 0,
    overflow: "hidden",
    position: "relative",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
      zIndex: 0,
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const listRowSx = (i) => ({
    backgroundColor: i % 2 ? ROW_ALT : "white",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.04)" },
  });

  return (
    <Box sx={{ p: 2, backgroundColor: BG }}>
      <Paper
        elevation={5}
        sx={{
          mx: "auto",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid rgba(25,118,210,0.15)",
        }}
      >
        {/* ✅ Header bar */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, lineHeight: 1.2 }}>
              Class Registration
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Register for available courses (and view registered courses)
            </Typography>
          </Box>
        </Box>

        {/* Snackbars */}
        {error && (
          <Snackbar open autoHideDuration={3000} onClose={() => setError("")}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          </Snackbar>
        )}
        {success && (
          <Snackbar open autoHideDuration={3000} onClose={() => setSuccess("")}>
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {success}
            </Alert>
          </Snackbar>
        )}

        {/* ✅ Body */}
        <Box sx={{ p: 2, backgroundColor: BG }}>
          <Grid container spacing={2}>
            {/* Available Courses */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={sectionCardSx}>
                <Box sx={{ position: "relative", zIndex: 1, p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                      Available Courses
                    </Typography>
                    <Chip
                      size="small"
                      label={`${availableCourses.length} available`}
                      sx={{ fontWeight: 900 }}
                    />
                  </Stack>

                  <Divider sx={{ my: 1.25, borderColor: "rgba(25,118,210,0.12)" }} />

                  {availableCourses.length > 0 ? (
                    <List sx={{ p: 0 }}>
                      {availableCourses.map((course, i) => (
                        <ListItem
                          key={course.CourseID}
                          divider
                          sx={{
                            ...listRowSx(i),
                            borderRadius: 2,
                            mb: 1,
                            border: `1px solid ${SURFACE_BORDER}`,
                          }}
                          secondaryAction={
                            <Button
                              variant="contained"
                              onClick={() => handleRegister(course.CourseID)}
                              sx={primaryBtnSx}
                            >
                              Register
                            </Button>
                          }
                        >
                          <ListItemText
                            primary={
                              <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                                {course.Semester} - {course.CourseName} (Section: {course.Section})
                              </Typography>
                            }
                            secondary={
                              <Typography sx={{ opacity: 0.8 }}>
                                Credits: {course.Credits}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography sx={{ opacity: 0.75 }}>No available courses.</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Registered Courses */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={sectionCardSx}>
                <Box sx={{ position: "relative", zIndex: 1, p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                      Registered Courses
                    </Typography>
                    <Chip
                      size="small"
                      label={`${registeredCourses.length} registered`}
                      sx={{ fontWeight: 900 }}
                    />
                  </Stack>

                  <Divider sx={{ my: 1.25, borderColor: "rgba(25,118,210,0.12)" }} />

                  {registeredCourses.length > 0 ? (
                    <List sx={{ p: 0 }}>
                      {registeredCourses.map((course, i) => (
                        <ListItem
                          key={course.CourseID}
                          divider
                          sx={{
                            ...listRowSx(i),
                            borderRadius: 2,
                            mb: 1,
                            border: `1px solid ${SURFACE_BORDER}`,
                          }}
                          secondaryAction={
                            <Button
                              variant="outlined"
                              disabled
                              onClick={() => handleDrop(course.CourseID)}
                              sx={{
                                ...outlinedBtnSx,
                                opacity: 0.6,
                                cursor: "not-allowed",
                              }}
                            >
                              Drop
                            </Button>
                          }
                        >
                          <ListItemText
                            primary={
                              <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                                {course.Semester} - {course.CourseName} (Section: {course.Section})
                              </Typography>
                            }
                            secondary={
                              <Typography sx={{ opacity: 0.8 }}>
                                Credits: {course.Credits}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography sx={{ opacity: 0.75 }}>No registered courses.</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default ClassRegistration;
